import React, { useEffect, useRef } from 'react';
import styled, { createGlobalStyle } from 'styled-components';

// Folded from the drawer prototype (mesa-workspace#25, VariantD — the winning
// variant): plain CSS `translateX` transition (no framer-motion bounce) +
// a dark backdrop scrim (its own plain-CSS opacity transition, to stay
// consistent with the "no bounce" feel), dismissible by backdrop click,
// outside click, or Esc.
//
// `disableDismiss` (mesa-workspace#26): pass `true` whenever something is
// visually stacked on top of this drawer (a Modal, or anything else) — its
// own Esc/outside-click handling would otherwise fire alongside the topmost
// layer's, closing both at once instead of just the one on top. The caller
// decides what "something on top" means; this component doesn't need to
// know what that something is, so it composes with any modal usage, not
// just one paired to a specific drawer item.
//
// Separately: never mount a `Modal` (or anything `position: fixed`) as a
// DOM descendant of this drawer's panel — `transform: translateX(...)`
// on the panel creates a new containing block, which breaks `position:
// fixed` positioning for anything nested inside it. Mount it as a sibling
// wherever this drawer is used.
//
// `modal={false}` drops the backdrop, scroll lock and outside-click dismissal
// so the page stays usable beside the drawer (e.g. picking another item in a
// list swaps the drawer's content). Esc still closes it.

const GlobalScrollLock = createGlobalStyle`
  body {
    overflow: hidden !important;
  }
`;

// Esc inside a form field belongs to the field (e.g. cancelling an inline edit), not the drawer.
function useEscToClose(onClose: () => void, active: boolean): void {
  useEffect(() => {
    if (!active) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !(e.target as HTMLElement).closest?.('input, textarea, select')) onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [active, onClose]);
}

// A non-modal drawer lets clicks through everywhere but its panel.
// Above page content (filter bars and their selects sit at 31), below the sticky nav (51) and #modal-root (60).
const DrawerOverlayRoot = styled.div<{ $open: boolean; $modal: boolean }>`
  position: fixed;
  inset: 0;
  z-index: 50;
  pointer-events: ${(p) => (p.$open && p.$modal ? 'auto' : 'none')};
`;

const DrawerBackdrop = styled.div<{ $open: boolean }>`
  position: fixed;
  inset: 0;
  background: rgba(14, 18, 27, 0.4);
  opacity: ${(p) => (p.$open ? 1 : 0)};
  pointer-events: ${(p) => (p.$open ? 'auto' : 'none')};
  transition: opacity 0.2s ease;
`;

// Pinned below the app header and to the bottom of the viewport, so long content scrolls fully into view.
const DrawerPanel = styled.div<{ $open: boolean; $width: number; $maxWidth?: string }>`
  position: fixed;
  top: 66px;
  bottom: 0;
  right: 0;
  width: ${(p) => p.$width}px;
  ${(p) => p.$maxWidth && `max-width: ${p.$maxWidth};`}
  pointer-events: auto;
  background: ${(p) => p.theme.colors.White};
  border-left: 1px solid ${(p) => p.theme.colors.Gray200};
  box-shadow: -8px 0 24px -8px rgba(14, 18, 27, 0.12);
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  transform: translateX(${(p) => (p.$open ? '0' : '100%')});
  transition: transform 0.2s ease;
`;

// Sticks to the top of the scrolling panel so the title and close button stay in view.
const DrawerHeader = styled.div`
  position: sticky;
  top: 0;
  z-index: 1;
  flex-shrink: 0;
  background: ${(p) => p.theme.colors.White};
  padding: 20px 24px;
  border-bottom: 1px solid ${(p) => p.theme.colors.Gray200};
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
`;

const DrawerTitle = styled.div`
  flex: 1;
  min-width: 0;
  font-size: 18px;
  font-weight: 600;
  color: ${(p) => p.theme.colors.Gray900};
`;

const DrawerBody = styled.div`
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: ${(p) => p.theme.colors.Gray500};
  font-size: 20px;
  line-height: 1;
  padding: 4px;

  &:hover {
    color: ${(p) => p.theme.colors.Gray900};
  }
`;

type EntityDrawerProps = {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly title?: React.ReactNode;
  readonly children?: React.ReactNode;
  readonly footer?: React.ReactNode;
  /** Disable outside-click/Esc dismissal while something else is stacked visually on top of this drawer. */
  readonly disableDismiss?: boolean;
  /** `false` keeps the page interactive beside the drawer: no backdrop, scroll lock or outside-click dismissal. */
  readonly modal?: boolean;
  readonly width?: number;
  /** Caps `width` on narrow screens, e.g. '67%' (of the viewport, as the panel is fixed). */
  readonly maxWidth?: string;
};

function EntityDrawer({
  open, onClose, title, children, footer, disableDismiss = false, modal = true, width = 800, maxWidth,
}: EntityDrawerProps): React.ReactElement {
  const panelRef = useRef<HTMLDivElement>(null);
  useEscToClose(onClose, open && !disableDismiss);

  useEffect(() => {
    if (!open || disableDismiss || !modal) return;
    const handler = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) onClose();
    };
    // Delay attaching by a tick so the click that opened the drawer doesn't
    // immediately bubble into this listener and close it again.
    const id = setTimeout(() => document.addEventListener('mousedown', handler), 0);
    return () => {
      clearTimeout(id);
      document.removeEventListener('mousedown', handler);
    };
  }, [open, disableDismiss, modal, onClose]);

  return (
    <DrawerOverlayRoot $open={open} $modal={modal}>
      {open && modal && <GlobalScrollLock />}
      {modal && <DrawerBackdrop $open={open} onClick={disableDismiss ? undefined : onClose} />}
      <DrawerPanel ref={panelRef} $open={open} $width={width} $maxWidth={maxWidth}>
        {open && (
          <>
            <DrawerHeader>
              <DrawerTitle>{title}</DrawerTitle>
              <CloseButton onClick={onClose} aria-label="Close">×</CloseButton>
            </DrawerHeader>
            <DrawerBody>
              {children}
              {footer}
            </DrawerBody>
          </>
        )}
      </DrawerPanel>
    </DrawerOverlayRoot>
  );
}

export default EntityDrawer;
